import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Palette, Layout, Layers, Sparkles, Wand2, Eye, 
  ArrowRight, Rocket, Calendar
} from "lucide-react";
import { SiFigma, SiFramer } from "react-icons/si";

export default function UiUxDesignService() {
  const capabilities = [
    {
      icon: <Layout className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "User Research & Wireframing",
      desc: "Deep user behavior analysis, wireframes mapping, user journeys, and interactive low-fidelity prototypes."
    },
    {
      icon: <Palette className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "UI & Mobile App Design",
      desc: "Pixel-perfect mobile & desktop interfaces with custom design systems, color palettes, and typography guidelines."
    },
    {
      icon: <Wand2 className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Logo & Brand Identity",
      desc: "Distinctive logo creation, vector iconography, brand style guides, social media kits, and visual assets."
    },
    {
      icon: <Layers className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Figma Design System Systems",
      desc: "Scalable component libraries, auto-layout tokens, dark/light variants, and handoff specs for engineering teams."
    },
    {
      icon: <Eye className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Interactive Prototyping",
      desc: "Realistic micro-interactions, animated transitions, user testing prototypes, and clickable Figma demos."
    },
    {
      icon: <Sparkles className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "UX Audit & Redesign",
      desc: "In-depth audit of existing web/app UI, identifying usability friction, accessibility (WCAG), and CRO improvements."
    }
  ];

  const designToolsData = [
    {
      name: "Figma",
      category: "Interface & Systems",
      icon: React.createElement(SiFigma as any, { className: "text-[#F24E1E]", size: 32 })
    },
    {
      name: "Adobe XD",
      category: "UI Wireframing",
      icon: (
        <div className="w-10 h-10 rounded-xl bg-[#470137] border border-[#FF61F6]/50 flex items-center justify-center font-black text-[#FF61F6] text-sm shadow-sm">
          Xd
        </div>
      )
    },
    {
      name: "Adobe Illustrator",
      category: "Vector & Logo Design",
      icon: (
        <div className="w-10 h-10 rounded-xl bg-[#330000] border border-[#FF9A00]/50 flex items-center justify-center font-black text-[#FF9A00] text-sm shadow-sm">
          Ai
        </div>
      )
    },
    {
      name: "Adobe Photoshop",
      category: "Graphics & Editing",
      icon: (
        <div className="w-10 h-10 rounded-xl bg-[#001E36] border border-[#31A8FF]/50 flex items-center justify-center font-black text-[#31A8FF] text-sm shadow-sm">
          Ps
        </div>
      )
    },
    {
      name: "Framer",
      category: "Interactive Prototypes",
      icon: React.createElement(SiFramer as any, { className: "text-[#0055FF]", size: 32 })
    },
    {
      name: "ProtoPie",
      category: "Micro-Interactions",
      icon: (
        <div className="w-10 h-10 rounded-xl bg-[#FF6B00] border border-amber-300/50 flex items-center justify-center font-black text-white text-sm shadow-sm">
          PP
        </div>
      )
    },
    {
      name: "Design Tokens",
      category: "UI Component Variables",
      icon: <Palette className="text-[#A855F7]" size={32} />
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* HERO */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Visual Excellence
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
            ✨ UI/UX & <span className="text-blue-600 dark:text-cyan-400">Logo Design</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            Crafting intuitive, user-centric web and mobile interfaces alongside memorable brand identity assets that captivate audiences and boost conversions.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link 
              to="/enquirynow" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white shadow-lg shadow-blue-600/25 transition-all cursor-pointer"
            >
              <Rocket size={18} className="!text-white" />
              <span className="!text-white font-bold">Request UI/UX Proposal</span>
              <ArrowRight size={16} className="!text-white" />
            </Link>

            <Link 
              to="/Meeting" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-blue-500 transition-all cursor-pointer shadow-sm"
            >
              <Calendar size={18} />
              <span>Book Design Discovery</span>
            </Link>
          </div>
        </div>

        {/* VISUAL DESIGN TECH STACK GRID */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Design Software Ecosystem
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              UI/UX Design Software & Prototyping Tools
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              We utilize industry-standard design tools, vector suites, and prototyping engines.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {designToolsData.map((tool, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.04, y: -2 }}
                className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 p-4 rounded-2xl flex flex-col items-center justify-center text-center space-y-2.5 transition-all shadow-sm hover:shadow-md hover:border-blue-500/40 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm">
                  {tool.icon}
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-900 dark:text-white leading-tight">{tool.name}</h4>
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 block mt-0.5">{tool.category}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CAPABILITIES */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Design Services & Deliverables
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">
              Comprehensive digital interface & brand identity services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -4 }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 space-y-4 shadow-md flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center">
                    {cap.icon}
                  </div>
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
                    {cap.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
                    {cap.desc}
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80">
                  <Link to="/enquirynow" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:underline">
                    <span>Enquire For Design</span>
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA BANNER */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-900/90 dark:to-indigo-950/90 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready for a World-Class UI/UX Redesign?
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto text-sm font-medium">
            Book a complimentary design review with our lead UI/UX designer. We will review your current product or wireframes and present a component roadmap.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link 
              to="/enquirynow" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-white text-blue-700 hover:bg-slate-100 shadow-md flex items-center gap-2 cursor-pointer"
            >
              <span>Get Design Proposal</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
